import React, { useState, useEffect } from 'react';
import './TokenStyles.css';

const AchievementPopup = ({ achievement, onClose, isFlowBonus = false }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Trigger token animation when achievement appears
    if (achievement && achievement.tokens > 0) {
      // Small delay to let popup render first
      setTimeout(() => {
        if (window.triggerTokenAnimation) {
          const popup = document.querySelector('.achievement-popup');
          window.triggerTokenAnimation(achievement.tokens, popup);
        }
      }, 300);
    }

    // Auto-hide after 5 seconds
    const timer = setTimeout(() => {
      setVisible(false);
      if (onClose) onClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [onClose, achievement]);

  if (!visible || !achievement) return null;

  // Map achievement types to display info
  const getAchievementInfo = () => {
    const types = {
      'streak': {
        icon: '🔥',
        title: 'Learning Streak!',
        description: `${achievement.days} day streak - Keep it up!`,
        color: '#F59E0B'
      },
      'flow_state': {
        icon: '✨',
        title: 'FLOW STATE!',
        description: 'You\'re in the zone! 3x token multiplier active!',
        color: '#F59E0B'
      },
      'breakthrough': {
        icon: '💡',
        title: 'Learning Breakthrough!',
        description: achievement.description || 'You had a moment of insight!',
        color: '#FBBF24'
      },
      'completion': {
        icon: '✅',
        title: 'Concept Mastered!',
        description: 'You\'ve mastered this concept!',
        color: '#10B981'
      },
      'bloom_milestone': {
        icon: '🎯',
        title: 'New Cognitive Level!',
        description: achievement.description || 'You\'ve reached a new level of thinking!',
        color: '#8B5CF6'
      },
      'misconception_cleared': {
        icon: '🧠',
        title: 'Misconception Cleared!',
        description: 'You\'ve overcome a misunderstanding!',
        color: '#3B82F6'
      },
      'recovery': {
        icon: '🌟',
        title: 'Recovery Bonus!',
        description: 'You took a break and came back stronger!',
        color: '#0EA5E9'
      },
      'referral': {
        icon: '👥',
        title: 'Referral Bonus!',
        description: 'Someone joined using your referral code!',
        color: '#10B981'
      },
      'first_login': {
        icon: '🎉',
        title: 'Welcome!',
        description: 'Welcome to Morpheus-RL! Enjoy your bonus tokens!',
        color: '#8B5CF6'
      }
    };

    return types[achievement.type] || {
      icon: '🪙',
      title: 'Achievement Unlocked!',
      description: 'You earned some tokens!',
      color: '#3498db'
    };
  };

  const info = getAchievementInfo();
  const isFlowAchievement = achievement.type === 'flow_state' || isFlowBonus;

  return (
    <div
      className="achievement-popup achievement-pop"
      style={{
        background: isFlowAchievement
          ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.95), rgba(249, 115, 22, 0.95))'
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: `2px solid ${isFlowAchievement ? 'rgba(245, 158, 11, 0.5)' : info.color}`,
        borderRadius: '16px',
        boxShadow: isFlowAchievement
          ? '0 8px 32px rgba(245, 158, 11, 0.4), 0 0 0 1px rgba(245, 158, 11, 0.1) inset'
          : '0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
        animation: isFlowAchievement ? 'achievementPop 0.5s ease-out, flowPulse 2s ease-in-out infinite' : 'achievementPop 0.5s ease-out',
        color: isFlowAchievement ? 'white' : '#2c3e50'
      }}
    >
      {/* Flow state golden glow */}
      {isFlowAchievement && (
        <div style={{
          position: 'absolute',
          inset: '-4px',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.4), rgba(249, 115, 22, 0.4))',
          borderRadius: '18px',
          filter: 'blur(16px)',
          opacity: 0.6,
          animation: 'flowGlow 2s ease-in-out infinite',
          pointerEvents: 'none',
          zIndex: -1
        }} />
      )}

      <div
        className="achievement-icon"
        style={{
          fontSize: '48px',
          filter: isFlowAchievement ? 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))' : 'none',
          animation: isFlowAchievement ? 'tokenSpin 1s ease-in-out' : 'none'
        }}
      >
        {info.icon}
      </div>

      <div className="achievement-content">
        <h3 style={{
          color: isFlowAchievement ? 'white' : info.color,
          fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
          fontSize: '20px',
          fontWeight: '700',
          letterSpacing: isFlowAchievement ? '0.5px' : 'normal',
          textShadow: isFlowAchievement ? '0 2px 8px rgba(0, 0, 0, 0.2)' : 'none'
        }}>
          {info.title}
        </h3>

        <p style={{
          color: isFlowAchievement ? 'rgba(255, 255, 255, 0.95)' : '#666',
          fontSize: '15px',
          fontWeight: isFlowAchievement ? '500' : 'normal'
        }}>
          {info.description}
        </p>

        <div
          className="token-reward"
          style={{
            background: isFlowAchievement
              ? 'rgba(255, 255, 255, 0.2)'
              : `${info.color}15`,
            border: isFlowAchievement
              ? '1px solid rgba(255, 255, 255, 0.3)'
              : `1px solid ${info.color}30`,
            borderRadius: '12px',
            padding: '8px 12px',
            marginTop: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: isFlowAchievement
              ? '0 4px 12px rgba(0, 0, 0, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}
        >
          <span
            className="token-icon"
            style={{
              fontSize: '20px',
              animation: isFlowAchievement ? 'tokenSpin 2s ease-in-out infinite' : 'none'
            }}
          >
            🪙
          </span>
          <span
            className="token-amount"
            style={{
              fontSize: '22px',
              fontWeight: '700',
              color: isFlowAchievement ? 'white' : info.color,
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.02em'
            }}
          >
            +{achievement.tokens}
          </span>
          <span
            className="token-label"
            style={{
              fontSize: '14px',
              color: isFlowAchievement ? 'rgba(255, 255, 255, 0.9)' : '#666',
              fontWeight: '500'
            }}
          >
            tokens
          </span>
        </div>

        {/* Multiplier badge for flow state */}
        {isFlowAchievement && (
          <div style={{
            marginTop: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            background: 'rgba(255, 255, 255, 0.25)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            borderRadius: '10px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: '700',
            color: 'white',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            🔥 3× Multiplier
          </div>
        )}
      </div>

      <button
        className="close-button"
        onClick={() => {
          setVisible(false);
          if (onClose) onClose();
        }}
        style={{
          color: isFlowAchievement ? 'rgba(255, 255, 255, 0.8)' : '#999',
          fontSize: '24px',
          transition: 'all 0.2s ease',
          ':hover': {
            color: isFlowAchievement ? 'white' : '#333'
          }
        }}
      >
        ×
      </button>
    </div>
  );
};

export default AchievementPopup;